import React, { useEffect, useRef, useState } from "react";
import { IFloatingFilterParams } from "ag-grid-community";
import { useDataTableContext } from "../shared/Context";
import { Overlay, Tooltip } from "react-bootstrap";
import { DEFAULT_INPUT_VALIDATION, DEFAULT_INPUT_VALIDATION_HINT, ID_INPUT_VALIDATION, ID_INPUT_VALIDATION_HINT, DEVEUI_INPUT_VALIDATION, DEVEUI_INPUT_VALIDATION_HINT, DEVADDR_INPUT_VALIDATION_HINT, DEVADDR_INPUT_VALIDATION, APP_ACC_GROUP_ID_INPUT_VALIDATION, APP_ACC_GROUP_ID_INPUT_VALIDATION_HINT } from "../../DataTable/DataTableConsts";

type Props = IFloatingFilterParams & {
  inputValidation?: RegExp | string;
  inputValidationHint?: string; 
  maxInputLength?: number;
  name?: string;
  defaultFilterValue?: string[];
  // filterParams may contain a mapper function that should be applied
  // to the raw input before sending it to the underlying filter model.
  filterParams?: {
    mapper?: (x: any) => any | Promise<any>;
  };
};

function toRegExp(val: RegExp | string | undefined) : RegExp {
  if (!val) return /.*/;
  if (val instanceof RegExp) return val;
  try {
    // Remove leading/trailing slashes if present
    let pattern = val;
    let flags = '';
    if (pattern.startsWith('/') && pattern.lastIndexOf('/') > 0) {
      const lastSlash = pattern.lastIndexOf('/');
      flags = pattern.slice(lastSlash + 1);
      pattern = pattern.slice(1, lastSlash);
    }
    return new RegExp(pattern, flags);
  } catch {
    return /.*/;
  }
}

const CustomTextboxFloatingFilter: React.FC<Props> = (props) => {
  const { name, defaultFilterValue = [], inputValidationHint, inputValidation } = props;
  const { floatingFilterValueMap } = useDataTableContext();
  const colId = props.column.getColId();
  const floatingFilterValueMapKey = `${name}${colId}`;
  const initialValue = floatingFilterValueMap.current.get(floatingFilterValueMapKey) ?? defaultFilterValue[0] ?? "";

  const regex: RegExp =  props?.inputValidation instanceof RegExp ? props?.inputValidation : toRegExp(props?.inputValidation);
  const maxLength: number | undefined = props.maxInputLength ?? undefined;

  const [inputValue, setInputValue] = useState(initialValue);
  const [searchedValue, setSearchedValue] = useState(initialValue);
  const [isInvalid, setIsInvalid] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const invalidIconRef = useRef<HTMLSpanElement | null>(null);
  const [showInvalidTooltip, setShowInvalidTooltip] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const placeholderName = props.column.getColDef().headerName;
  const placeholder = `Search ${placeholderName}`;

  // Compute hint: if not provided, use the known hint if regex matches a known one
  let computedHint = inputValidationHint;
  if (!computedHint && inputValidation) {
    const val = inputValidation instanceof RegExp ? inputValidation?.source : inputValidation;
    if (val === DEFAULT_INPUT_VALIDATION) {
      computedHint = DEFAULT_INPUT_VALIDATION_HINT;
    } else if (val === ID_INPUT_VALIDATION) {
      computedHint = ID_INPUT_VALIDATION_HINT;
    } else if (val === DEVEUI_INPUT_VALIDATION) {
      computedHint = DEVEUI_INPUT_VALIDATION_HINT;
    }
    else if (val === DEVADDR_INPUT_VALIDATION) {
      computedHint = DEVADDR_INPUT_VALIDATION_HINT;
    }
    else if (val === APP_ACC_GROUP_ID_INPUT_VALIDATION) {
      computedHint = APP_ACC_GROUP_ID_INPUT_VALIDATION_HINT;
    }
  }
  if (!computedHint) computedHint = "Invalid format";

  useEffect(() => {
    if (initialValue) {
      const floatingFilterValueMapKey = `${name}${colId}`;
      floatingFilterValueMap.current.set(floatingFilterValueMapKey, initialValue);
      // Apply mapper (if provided) before setting the underlying filter model.
      props.parentFilterInstance((filterInstance) => {
        try {
          const maybeMapped = props.filterParams?.mapper ? props.filterParams.mapper(initialValue) : initialValue;
          if (maybeMapped && typeof (maybeMapped as any).then === "function") {
            // mapper returned a Promise
            (maybeMapped as Promise<any>).then((mapped) => {
              filterInstance.setModel({ type: "equals", filter: mapped });
            }).catch(() => {
              // fallback to raw value on mapper error
              filterInstance.setModel({ type: "equals", filter: initialValue });
            });
          } else {
            filterInstance.setModel({ type: "equals", filter: maybeMapped });
          }
        } catch (err) {
          // If mapper throws, fall back to raw value
          filterInstance.setModel({ type: "equals", filter: initialValue });
        }
      });
      // api.onFilterChanged(); // Trigger filtering 
    }
  }, []);
  
    useEffect(() => {
      const floatingFilterValueMapKey = `${name}${colId}`;
      setInputValue(floatingFilterValueMap.current.get(floatingFilterValueMapKey) ?? "");
    }, [name]);
    
  const applyFilter = (filterValue: string) => {
    props.parentFilterInstance((instance) => {
      if (filterValue === "") {
        instance.setModel(null);
      } else {
        try {
          const maybeMapped = props.filterParams?.mapper ? props.filterParams.mapper(filterValue) : filterValue;
          if (maybeMapped && typeof (maybeMapped as any).then === "function") {
            (maybeMapped as Promise<any>).then((mapped) => {
              instance.setModel({ type: "equals", filter: mapped });
              props.api.onFilterChanged();
            }).catch(() => {
              instance.setModel({ type: "equals", filter: filterValue });
              props.api.onFilterChanged();
            });
            // return early since onFilterChanged invoked in promise handlers
            const floatingFilterValueMapKey = `${name}${colId}`;
            floatingFilterValueMap.current.set(floatingFilterValueMapKey, filterValue);
            return;
          } else {
            instance.setModel({ type: "equals", filter: maybeMapped });
          }
        } catch (err) {
          instance.setModel({ type: "equals", filter: filterValue });
        }
      }
      const floatingFilterValueMapKey = `${name}${colId}`;
      floatingFilterValueMap.current.set(floatingFilterValueMapKey, filterValue);
      props.api.onFilterChanged();
    });
    setSearchedValue(filterValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && inputValue !== searchedValue) {
      if (isInvalid) {        // Prevent search if invalid
        return;
      }
      applyFilter(inputValue);
    }
  };

  const handleClear = () => {
    if (searchedValue !== "") {
      applyFilter("");
      setSearchedValue("");
    }
    setInputValue("");
    setIsInvalid(false);
  };

  // Hide tooltip after 2 seconds of focus, unless invalid tooltip is open
  useEffect(() => {
    if (isFocused) {
      if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
      // Only set timeout if invalid tooltip is not open
      if (!showInvalidTooltip) {
      tooltipTimeoutRef.current = setTimeout(() => {
        setIsFocused(false);
      }, 2000);
      }
    } else {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
    }
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
    };
  }, [isFocused, showInvalidTooltip]);

  // Soft validation: allow all input, but flag as invalid if regex fails
  useEffect(() => {
    if (inputValue === "") {
      setIsInvalid(false);
    } else {
      setIsInvalid(!regex.test(inputValue));
    }
  }, [inputValue, regex]);

  return (
    <div style={{ display: "flex", gap: "0px", width: "100%" }}>
      <div style={{ position: "relative", flex: 1 }}>
        <input
          ref={inputRef}
          type="text"
          maxLength={maxLength}
          className="ag-input-field-input ag-text-field-input"
          value={inputValue}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(e) => {
            const value = e.target.value;
              setInputValue(value);
              if (value === "" && searchedValue !== "") {
                setSearchedValue("");
                applyFilter("");
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            width: "100%",
            paddingLeft: "10px",
            paddingRight: inputValue && isInvalid ? "40px" : "24px",
            borderColor: isInvalid ? "red" : undefined,
          }}
          aria-invalid={isInvalid}
        />
        {/* Tooltip on focus, with regex hint if provided */}
        <Overlay
          target={inputRef.current}
          show={isFocused}
          placement="bottom"
        >
          {(props) => (
            <Tooltip id={`tooltip-search-enter`} {...props}>
              {`Press Enter to search`}
            </Tooltip>
          )}
        </Overlay>
        {/* Show invalid icon if input is invalid, with tooltip on hover/focus */}
        {inputValue && isInvalid && (
          <>
            <span
              ref={invalidIconRef}
              style={{
                position: "absolute",
                right: "22px",
                top: "12px",
                transform: "translateY(-50%)",
                color: "red",
                fontSize: "12px",
                pointerEvents: "auto",
                zIndex: 3,
              }}
              onMouseEnter={() => {
                setShowInvalidTooltip(true);
                setIsFocused(false);
              }}
              onMouseLeave={() => setShowInvalidTooltip(false)}
              onFocus={() => {
                setShowInvalidTooltip(true);
                setIsFocused(false);
              }}
              onBlur={() => setShowInvalidTooltip(false)}
              tabIndex={0}
              aria-label={computedHint}
            >
              <i className="fas fa-exclamation-circle"></i>
            </span>
            <Overlay
              target={inputRef.current}
              show={showInvalidTooltip}
              placement="bottom"
            >
              {(overlayProps) => (
                <Tooltip id={`tooltip-invalid-icon`} {...overlayProps}>
                  {computedHint}
                </Tooltip>
              )}
            </Overlay>
          </>
        )}
        {/* Clear button */}
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              position: "absolute",
              right: "0px",
              top: "12px",
              transform: "translateY(-50%)",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontSize: "14px",
              color: "#888",
              zIndex: 2,
            }}
            aria-label="Clear"
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>
    </div>
  );
};

export default CustomTextboxFloatingFilter;